<template>
  <div
    :class="{
      'has-backdrop-blur': backdropBlur,
      [MAX_WIDTH_ALIGNED_CLASS]: isMaxWidthAligned(containerStyle),
    }"
    :style="containerStyle"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  fadeColor,
  getSpacingStyleData,
  getMaxWidthMarginStyleData,
  getColorStyleData,
  getOpacityPercent,
  getSizeStyleData,
} from '../../../composables/useStyleData'
import { MAX_WIDTH_ALIGNED_CLASS, isMaxWidthAligned } from '../../../lib/maxWidthAlign'
import type { ContainerFields } from './definition'

const props = withDefaults(
  defineProps<{
    fields?: ContainerFields
  }>(),
  {
    fields: () => ({}),
  },
)

const backdropBlur = computed(() => {
  const px = props.fields?.backdropBlur
  return px && px > 0 ? `${px}px` : ''
})

const containerStyle = computed(() => {
  const f = props.fields
  const s: Record<string, string> = {}
  const layout = f?.layout || 'block'

  if (layout !== 'block') {
    s.display = 'flex'
    s.flexDirection = layout
    const gap = getSpacingStyleData(f?.gap)
    if (gap) s.gap = gap
    if (f?.alignItems) s.alignItems = f.alignItems
    if (f?.justifyContent) s.justifyContent = f.justifyContent
    if (f?.wrap) s.flexWrap = 'wrap'
  }

  const width = getSizeStyleData(f?.width)
  if (width) s.width = width
  const minWidth = getSizeStyleData(f?.minWidth)
  if (minWidth) s.minWidth = minWidth
  const maxWidth = getSizeStyleData(f?.maxWidth)
  if (maxWidth) s.maxWidth = maxWidth
  const height = getSizeStyleData(f?.height)
  if (height) s.height = height
  const minHeight = getSizeStyleData(f?.minHeight)
  if (minHeight) s.minHeight = minHeight
  const padding = getSpacingStyleData(f?.padding)
  if (padding) s.padding = padding
  // The authored margin, and a capped box placed by its alignment, centred when unset
  // (render/lib/maxWidthAlign.ts).
  Object.assign(s, getMaxWidthMarginStyleData(f?.margin, maxWidth, f?.maxWidthAlign, 'center'))
  const bgColor = fadeColor(getColorStyleData(f?.bgColor), getOpacityPercent(f?.bgOpacity))
  if (bgColor) s.backgroundColor = bgColor
  const borderWidth = getSizeStyleData(f?.borderWidth)
  if (borderWidth) s.borderWidth = borderWidth
  const borderColor = getColorStyleData(f?.borderColor)
  if (borderColor) s.borderColor = borderColor
  if (borderWidth || borderColor) s.borderStyle = 'solid'
  const borderRadius = getSizeStyleData(f?.borderRadius)
  if (borderRadius) s.borderRadius = borderRadius
  if (backdropBlur.value) s['--container-backdrop-blur'] = backdropBlur.value

  return s
})
</script>

<style scoped>
.has-backdrop-blur {
  -webkit-backdrop-filter: blur(var(--container-backdrop-blur));
  backdrop-filter: blur(var(--container-backdrop-blur));
}
</style>
